import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PrefetchLink from '../common/PrefetchLink';
import Footer from './Footer';
import { useTemplates } from '../../context/TemplatesContext';
import { useAuth } from '../../context/AuthContext';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';
import './Sidebar.css';
import defaultLogo from '../../assets/logo.png';
import { FaHome, FaCaretDown, FaSignOutAlt, FaBars, FaTimes } from 'react-icons/fa';
// BIGLI-11：導覽清單改由 constants/navigation.js 單一來源提供，側欄與導覽總覽頁
// （pages/Home.js）引用同一份，避免兩邊各自硬編而不同步。
import { NAV_GROUPS, findActiveGroupKey, buildAccountNavItems } from '../../constants/navigation';
import { getTransferPendingCounts } from '../../services/lineApi';
import { TRANSFER_PENDING_EVENT } from '../../utils/lineInquiry';
// BIGLI-110：「LINE 新需求」未讀紅點（60 秒輪詢＋切回分頁立即查）
import useLineUnreadCount from '../../hooks/useLineUnreadCount';
import { formatUnreadBadge } from '../../utils/lineUnread';

const Sidebar = () => {
  const { templates } = useTemplates();
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const sidebarRef = useRef(null);
  const navDropdownRefs = useRef({});
  // BIGLI-111：「LINE 新需求」轉入待接手數量徽章（換頁或轉單／接手後重抓）
  const [linePending, setLinePending] = useState(0);
  useEffect(() => {
    if (!currentUser) return undefined;
    let alive = true;
    const load = () => Promise.resolve()
      .then(() => getTransferPendingCounts())
      .then((r) => { if (alive) setLinePending(Number(r?.total) || 0); })
      .catch(() => {});
    load();
    window.addEventListener(TRANSFER_PENDING_EVENT, load);
    return () => { alive = false; window.removeEventListener(TRANSFER_PENDING_EVENT, load); };
  }, [currentUser, location.pathname]);
  const { counts: lineUnread } = useLineUnreadCount(Boolean(currentUser));
  const lineBadge = formatUnreadBadge(lineUnread.total);
  // BIGLI-113：LINE 新需求併入「報價/詢價管理」，群組層級紅點改掛在 quotation 群組上
  const badgeOf = (groupKey) => (groupKey === 'quotation' ? lineBadge : '');

  // Use uploaded logo if available, otherwise use default
  const logoSrc = templates?.companyLogoBase64 || defaultLogo;

  // Use company name from templates (unified source)
  const companyName = templates?.companyName || currentUser?.companyName || '公司';

  // 個人資料／用戶管理／公司基本資料的權限規則同樣收斂到 constants/navigation.js，
  // 側欄與導覽總覽頁揭露的項目必然一致。
  const accountNavItems = buildAccountNavItems(currentUser);

  const pathname = location.pathname;
  const activeGroupKey = findActiveGroupKey(pathname);
  const isHomeActive = pathname === '/';

  // 切換到某個群組底下的頁面時，自動把該群組的 accordion 展開。
  useEffect(() => {
    if (activeGroupKey) {
      setOpenNavDropdown(activeGroupKey);
    }
  }, [activeGroupKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile drawer when clicking outside or on ESC key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const hamburger = document.querySelector('.hamburger-menu');
        if (hamburger && !hamburger.contains(event.target)) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Prevent body scroll when mobile drawer is open（使用全域引用計數）
    if (isMobileMenuOpen) {
      lockScroll();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      if (isMobileMenuOpen) {
        unlockScroll();
      }
    };
  }, [isMobileMenuOpen]);

  // Close nav accordions when clicking outside the sidebar / ESC
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openNavDropdown) return;
      const isClickInsideDropdown = Object.values(navDropdownRefs.current).some(ref =>
        ref && ref.contains(event.target)
      );
      if (!isClickInsideDropdown) {
        setOpenNavDropdown(null);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && openNavDropdown) {
        setOpenNavDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [openNavDropdown]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    logout();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleNavDropdown = (menuName) => {
    setOpenNavDropdown(openNavDropdown === menuName ? null : menuName);
  };

  const isItemActive = (to) => pathname === to;

  return (
    <>
      {/* 漢堡選單按鈕 (僅在 ≤1024px 顯示，固定於左上角，與左側抽屜同側) */}
      <button
        className="hamburger-menu"
        onClick={toggleMobileMenu}
        aria-label="選單"
        aria-expanded={isMobileMenuOpen}
      >
        {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        {/* BIGLI-110：側欄收合（≤1024px 抽屜關著）時，紅點掛在選單按鈕上 */}
        {!isMobileMenuOpen && lineBadge && <span className="nav-unread-dot" data-testid="hamburger-unread-dot" aria-label={`LINE 新需求 ${lineBadge} 筆未讀`} />}
      </button>

      {/* 遮罩層 */}
      {isMobileMenuOpen && <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>}

      <aside
        className={`sidebar ${isMobileMenuOpen ? 'mobile-open' : ''}`}
        ref={sidebarRef}
        aria-label="主導覽"
      >
        <div className="sidebar-logo">
          <PrefetchLink to="/" onClick={closeMobileMenu}>
            <img src={logoSrc} alt="公司 LOGO" />
          </PrefetchLink>
        </div>

        <nav className="sidebar-nav">
          <ul>
            <li>
              <PrefetchLink
                to="/"
                className={`sidebar-link ${isHomeActive ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                {/* BIGLI-11：/ 已不是儀表板而是導覽總覽頁，項目名稱一併正名為「首頁」 */}
                <FaHome /> 首頁
              </PrefetchLink>
            </li>

            {NAV_GROUPS.map((group) => {
              const Icon = group.icon;
              const isOpen = openNavDropdown === group.key;
              const badge = badgeOf(group.key);
              return (
                <li
                  className="nav-dropdown"
                  key={group.key}
                  ref={el => navDropdownRefs.current[group.key] = el}
                >
                  <button
                    type="button"
                    className={`nav-dropdown-toggle ${activeGroupKey === group.key ? 'active' : ''}`}
                    onClick={() => toggleNavDropdown(group.key)}
                    aria-expanded={isOpen}
                  >
                    <span>
                      <span className="nav-icon-wrap">
                        <Icon />
                        {badge && <span className="nav-unread-dot" data-testid={`${group.key}-unread-icon-dot`} />}
                      </span>
                      {' '}{group.label}
                      {/* BIGLI-113：展開時數字改由 LINE 新需求子項目顯示，群組標題只留 icon 紅點，避免 220px 側欄折行 */}
                      {badge && !isOpen && <span className="nav-unread-badge" data-testid={`${group.key}-unread-badge`} aria-label={`${badge} 筆未讀`}>{badge}</span>}
                    </span>
                    <FaCaretDown className={isOpen ? 'rotate' : ''} />
                  </button>
                  {isOpen && (
                    <ul className="nav-dropdown-menu">
                      {group.items.map((item) => (
                        <li key={item.to}>
                          <PrefetchLink
                            to={item.to}
                            className={isItemActive(item.to) ? 'active' : ''}
                            onClick={closeMobileMenu}
                          >
                            {item.label}
                            {/* BIGLI-113：子項目層級的未讀紅點與轉入待接手徽章都只掛在 LINE 新需求這一行 */}
                            {item.to === '/line-inquiries' && lineBadge && (
                              <span className="nav-unread-badge" data-testid="line-inquiries-unread-badge" aria-label={`${lineBadge} 筆未讀`}>{lineBadge}</span>
                            )}
                            {item.to === '/line-inquiries' && linePending > 0 && (
                              <span className="nav-count-badge" title="轉入待接手" data-testid="nav-line-pending">{linePending}</span>
                            )}
                          </PrefetchLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {currentUser && (
          <div className="sidebar-user" ref={dropdownRef}>
            <button type="button" className="user-menu-toggle" onClick={toggleDropdown} aria-expanded={isDropdownOpen}>
              <span>{companyName} - {currentUser.name}</span>
              <FaCaretDown className={isDropdownOpen ? 'rotate' : ''} />
            </button>
            {isDropdownOpen && (
              <ul className="dropdown-menu">
                {/* 個人資料／用戶管理／公司基本資料的揭露條件見 constants/navigation.js */}
                {accountNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <PrefetchLink
                        to={item.to}
                        onClick={() => { setIsDropdownOpen(false); closeMobileMenu(); }}
                      >
                        <Icon /> {item.label}
                      </PrefetchLink>
                    </li>
                  );
                })}
                <li>
                  <button onClick={handleLogout}>
                    <FaSignOutAlt /> 登出
                  </button>
                </li>
              </ul>
            )}
          </div>
        )}

        {/* BIGLI-3：版權列併入側欄底部，桌機版不再有全域橫向 Footer。 */}
        <Footer />
      </aside>
    </>
  );
};

export default Sidebar;
